import { DEFAULT_CONNECTION_ROUTING_SETTINGS, normalizeConnectionSpacing } from './connectionRoutingSettings'

/**
 * Pure geometry helpers for the circular badge widget.
 *
 * The model intentionally stays simple: a badge is a circle with a center,
 * radius and bounding box derived from the object geometry. Attachment points
 * are computed deterministically from that circle, not from Konva state.
 */

export type BadgeSide = 'left' | 'right' | 'top' | 'bottom'

export interface BadgeGeometryInput {
    x: number
    y: number
    width: number
    height: number
}

export interface BadgeGeometry {
    centerX: number
    centerY: number
    radius: number
    left: number
    right: number
    top: number
    bottom: number
}

export interface BadgeAttachmentPoint {
    x: number
    y: number
}

export interface BadgeLayout {
    circle: BadgeGeometry
    icon: { x: number; y: number; width: number; height: number; fontSize: number }
    text: { x: number; y: number; width: number; height: number }
}

function num(value: unknown, fallback: number): number {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
}

export function getBadgeGeometry(badge: BadgeGeometryInput): BadgeGeometry {
    const x = num(badge.x, 0)
    const y = num(badge.y, 0)
    const width = Math.max(0, num(badge.width, 0))
    const height = Math.max(0, num(badge.height, 0))
    const radius = Math.min(width, height) / 2
    return {
        centerX: x + width / 2,
        centerY: y + height / 2,
        radius,
        left: x + width / 2 - radius,
        right: x + width / 2 + radius,
        top: y + height / 2 - radius,
        bottom: y + height / 2 + radius,
    }
}

export function getBadgeLayout(badge: BadgeGeometryInput, padding = 0, fontSize = 16): BadgeLayout {
    const circle = getBadgeGeometry(badge)
    const inset = Math.max(0, num(padding, 0))
    const contentRadius = Math.max(0, circle.radius - inset)
    const contentWidth = contentRadius * 1.5
    const x = circle.centerX - contentWidth / 2
    const top = circle.centerY - contentRadius * 0.62
    const iconHeight = contentRadius * 0.58
    return {
        circle,
        icon: {
            x, y: top, width: contentWidth, height: iconHeight,
            fontSize: Math.max(10, Math.round(contentRadius * 0.46)),
        },
        text: {
            x, y: top + iconHeight, width: contentWidth,
            height: Math.max(fontSize, contentRadius * 0.78),
        },
    }
}

const MAX_ATTACHMENT_ANGLE = Math.PI / 6

function sideAngle(side: BadgeSide, offset: number): number {
    switch (side) {
        case 'top':
            return -Math.PI / 2 + offset
        case 'right':
            return offset
        case 'bottom':
            return Math.PI / 2 - offset
        case 'left':
        default:
            return Math.PI - offset
    }
}

export function getBadgeAttachmentPoint(
    badge: BadgeGeometryInput,
    side: BadgeSide,
    index: number,
    count: number,
    connectionSpacing = DEFAULT_CONNECTION_ROUTING_SETTINGS.connection_spacing,
): BadgeAttachmentPoint {
    const geom = getBadgeGeometry(badge)
    return getBadgeAttachmentPointFromGeometry(geom, side, index, count, connectionSpacing)
}

export function getBadgeAttachmentPointFromGeometry(
    geom: BadgeGeometry,
    side: BadgeSide,
    index: number,
    count: number,
    connectionSpacing = DEFAULT_CONNECTION_ROUTING_SETTINGS.connection_spacing,
): BadgeAttachmentPoint {
    const radius = geom.radius
    const safeCount = Math.max(1, Math.round(count))
    const safeIndex = Math.min(safeCount - 1, Math.max(0, Math.round(index)))
    const spacing = normalizeConnectionSpacing(connectionSpacing)
    const maxAxisOffset = radius * Math.sin(MAX_ATTACHMENT_ANGLE)
    const axisOffset = Math.max(-maxAxisOffset, Math.min(maxAxisOffset,
        (safeIndex - (safeCount - 1) / 2) * spacing))
    const offset = radius > 0 ? Math.asin(axisOffset / radius) : 0
    const angle = sideAngle(side, offset)
    return {
        x: geom.centerX + Math.cos(angle) * radius,
        y: geom.centerY + Math.sin(angle) * radius,
    }
}
